import { NavLink, Outlet } from "react-router-dom";
import { Typography } from "@/components/atoms";
import { Tabs } from "@/components/molecules";
import { useClientScope } from "@/components/pages/Client/settings/ClientScopeContext";
import { clsx } from "@/utils";

const settingsLinks = [
  { name: "Client Profile", to: "profile" },
  { name: "Email Notifications", to: "notifications" },
  { name: "Payment Settings", to: "payments" },
];

/**
 * Settings tab of the client-portal Manage-Client takeover. Renders the same
 * sub-navigation as `ClientSettings` (Client Profile / Email Notifications /
 * Payment Settings — no Assistants, no Admin Settings), scoped to the managed
 * client. Account scoping (`ClientScopeProvider`) is supplied by the takeover
 * shell, so the reused settings screens under `<Outlet />` already target the
 * managed account — this tab must NOT re-wrap it (differs from the operator
 * precedent, where each tab wraps its own provider).
 */
export const ManageClientSettings = () => {
  const { isViewOnly } = useClientScope();

  return (
    <div className="flex flex-1 flex-col">
      {/* Self-cap at 1164 (mirrors the standalone ClientSettings, DEV-1366) — narrower than the
          takeover's 2360 body shell; a no-op below the cap (DEV-1367). */}
      <div className="mx-auto w-full max-w-[1164px] px-3 pb-12 pt-4 lg:px-5">
        <Typography variant="h2" className="mb-3 block">
          Settings
        </Typography>

        <div className="mb-3 lg:hidden">
          <Tabs tabs={settingsLinks} mobileVariant="sheet" />
        </div>

        <div className="rounded-lg border border-neutral-mid-gray bg-white lg:flex">
          <nav className="hidden shrink-0 flex-col border-r border-neutral-gray p-5 lg:flex" aria-label="Client settings">
            {settingsLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) =>
                  clsx("rounded-lg px-4 py-3 transition-colors", {
                    "bg-primary-light": isActive,
                    "hover:bg-neutral-surface-gray": !isActive,
                  })
                }
              >
                {({ isActive }) => (
                  <Typography
                    variant={isActive ? "action" : "paragraph"}
                    className={clsx("!m-0 whitespace-nowrap", isActive ? "text-primary" : "text-neutral-black")}
                  >
                    {link.name}
                  </Typography>
                )}
              </NavLink>
            ))}
          </nav>

          {/* View-only access disables all form controls in the reused settings panels (parity with operator DEV-1177). */}
          <fieldset disabled={isViewOnly} className="min-w-0 flex-1 p-5">
            <Outlet />
          </fieldset>
        </div>
      </div>
    </div>
  );
};
